import React, {useEffect, useState} from 'react';
import {
  Button,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import {useTranslation} from 'react-i18next';

import {ContactType, Domain, OrganisationKind} from '@store/common/organisation.enums';
import {ManageContact, ManageOrganisationUnit} from '@store/common/organisation.model';
import {clientDetailsQuery} from '@store/clients/details';
import {supplierDetailsQuery} from '@store/suppliers/details';

import {checkIfErrors, FieldValidationType, getFieldError} from '@utils/yup.utils';

import {Add, Delete} from '@icons/index';

import TextFieldComponent from '@components/inputs/textField/TextField.component';
import MultiDatePickerComponent from '@components/inputs/multiDatePicker/MultiDatePicker.component';
import MoreOptionsComponent from '@components/moreOptions/MoreOptions.component';
import DeleteContactModal from '@components/unitDetails/deleteContact/DeleteContact.modal';

import {Colors} from '@constants/colors.constants';
import {Sizes} from '@constants/sizes.constants';
import {FacturationMode} from '@store/clients/details/facturationMode/facturation-mode.model';
import EditIcon from '@mui/icons-material/Edit';
import MultiSelectComponent from '@components/inputs/multiSelect/MultiSelect.component';
import CheckboxComponent from '@components/inputs/checkbox/Checkbox.component';


interface OperationalUnitFormProps {
  unit?: ManageOrganisationUnit;
  handleCancel: () => void;
  handleDeleteContact?: (contactId?: number | string, successCallback?: () => void) => void;
  handleManageContact?: (manageContact: ManageContact | boolean) => void;
  handleFacturationMode?: (facturationMode: FacturationMode) => void;
  handleSave: (unit: ManageOrganisationUnit) => void;
  isSupplierUnit?: boolean;
  loading?: boolean;
  moreOptions?: JSX.Element[];
  readOnly?: boolean;
}

const OperationalUnitForm = (props: OperationalUnitFormProps) => {
  const {
    unit: unitProps,
    handleCancel,
    handleDeleteContact,
    handleManageContact,
    handleFacturationMode,
    handleSave,
    isSupplierUnit,
    loading,
    moreOptions,
    readOnly
  } = props;

  const { t } = useTranslation();

  const [showDeleteContact, setShowDeleteContact] = useState<ManageContact | false>(false);

  // const [openBillingMethod, setOpenBillingMethod] = useState<ManageBillingMethod | boolean>(false);
  const [unit, setUnit] = useState<ManageOrganisationUnit>({
    rcNumber: !isSupplierUnit ? clientDetailsQuery.clientRCNumber : undefined,
    domains: isSupplierUnit ? supplierDetailsQuery.supplierDomain : clientDetailsQuery.clientDomain,
  });

  useEffect(() => {
    if (!!unitProps && unit.status !== unitProps.status) {
      setUnit(unitProps);
    }
    if (isSupplierUnit && unitProps && JSON.stringify(unit.supplierOrganisationUnitContacts) !== JSON.stringify(unitProps.supplierOrganisationUnitContacts)) {
      setUnit((state) => ({ ...state, supplierOrganisationUnitContacts: unitProps.supplierOrganisationUnitContacts }));
    }
    if (!isSupplierUnit && unitProps && JSON.stringify(unit.clientOrganisationUnitContacts) !== JSON.stringify(unitProps.clientOrganisationUnitContacts)) {
      setUnit((state) => ({ ...state, clientOrganisationUnitContacts: unitProps.clientOrganisationUnitContacts }));
    }
  }, [isSupplierUnit, unit, unitProps]);

  const updateUnit = (name: string) => (value: any) => setUnit((state) => ({ ...state, [name]: value }));
  const updateUnitAddress = (name: string) => (value: any) => setUnit((state) => ({
    ...state,
    address: { ...state.address, [name]: value }
  }));

  const errors = !readOnly ? {
    name: getFieldError(unit.name, FieldValidationType.REQUIRED_STRING),
    address: {
      street: getFieldError(unit.address?.street, FieldValidationType.REQUIRED_STRING),
      zip: getFieldError(unit.address?.zip, FieldValidationType.REQUIRED_STRING),
      city: getFieldError(unit.address?.city, FieldValidationType.REQUIRED_STRING),
      country: getFieldError(unit.address?.country, FieldValidationType.REQUIRED_STRING),
    },
    unitKinds: isSupplierUnit ? getFieldError(unit.unitKinds, FieldValidationType.REQUIRED_ARRAY) : undefined,
    region: getFieldError(unit.region, FieldValidationType.REQUIRED_STRING),
  } : {};

  const contacts = unit[isSupplierUnit ? 'supplierOrganisationUnitContacts' : 'clientOrganisationUnitContacts'];
  const facturationMode = !isSupplierUnit ? unit.facturationMode : undefined;

  return (
    <>
      <Stack overflow="auto" py="20px" className="scrollable" flex={1}>
        <Stack component={Paper} elevation={0} p="20px" pt={!unitProps ? '20px' : '10px'} flex={1}>
          <Stack
            direction="row" alignItems="center" justifyContent="space-between"
            mb={!unitProps ? '20px' : '10px'}>
            <Typography fontSize={20} fontWeight="700">
              {t('unitDetails.title.operationalUnit')}
            </Typography>
            {!!unitProps && !!moreOptions && <MoreOptionsComponent options={moreOptions} />}
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal" sx={{ borderColor: Colors.background }} />
          <Stack direction="row" py="20px" spacing={7}>
            <Stack flex={1} spacing={3}>
              <Typography fontSize={18} fontWeight="700">
                {t('unitDetails.administrativeInformation.title')}
              </Typography>
              <Stack spacing={2}>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.denomination')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnit('name')}
                  value={unit.name || ''}
                  error={errors.name}
                  readOnly={readOnly}
                  required />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.marketingName')}
                  placeholder={t('global.writeSomething')}
                  readOnly={readOnly} />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.address')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnitAddress('street')}
                  value={unit.address?.street || ''}
                  error={errors.address?.street}
                  readOnly={readOnly}
                  required />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.zipCode')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnitAddress('zip')}
                  value={unit.address?.zip || ''}
                  error={errors.address?.zip}
                  readOnly={readOnly}
                  required />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.city')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnitAddress('city')}
                  value={unit.address?.city || ''}
                  error={errors.address?.city}
                  readOnly={readOnly}
                  required />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.country')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnitAddress('country')}
                  value={unit.address?.country || ''}
                  error={errors.address?.country}
                  readOnly={readOnly}
                  required />
                {!isSupplierUnit && (
                  <TextFieldComponent
                    labelWidth={Sizes.unitDetailsInputLabelWidth}
                    label={t('unitDetails.administrativeInformation.RCNumber')}
                    placeholder={t('global.writeSomething')}
                    handleChange={updateUnit('rcNumber')}
                    value={unit.rcNumber || ''}
                    readOnly={readOnly} />
                )}
                { !!isSupplierUnit && (<MultiSelectComponent
                    labelWidth={Sizes.unitDetailsInputLabelWidth}
                    color="primary"
                    forceBackgroundColor={true}
                    label={t('unitDetails.administrativeInformation.typology')}
                    placeholder={t('global.selectOption')}
                    handleChange={updateUnit('unitKinds')}
                    readOnly={false}
                    renderValue={(selected) => {
                      if (selected?.length <= 0 || selected[0].toString() === '') {
                        return <Typography color={Colors.textWithOpacity} >{t('global.selectOption')}</Typography>;
                      }
                      const selectedLabels = selected.map((value) => {
                        const selectedItem = OrganisationKind.selectItems().find((item) => item.value === value);
                        return selectedItem ? selectedItem.label : '';
                      });

                      return selectedLabels.join(', ');
                    }}
                    forceRenderValue={true}
                    values={unit?.unitKinds ?? undefined}
                    error={errors.unitKinds}
                    items={OrganisationKind.selectItems().map((sk) => {
                      return {value: sk.value, label: sk.label};
                    })}/>)}
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.administrativeInformation.invoicingAdditionalInformation')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnit('invoicingAdditionalInformation')}
                  value={unit.invoicingAdditionalInformation || ''}
                  readOnly={readOnly}
                />
              </Stack>
            </Stack>
            <Stack flex={1} spacing={3}>
              <Typography fontSize={18} fontWeight="700">
                {t('unitDetails.contactInformation.title')}
              </Typography>
              <Stack spacing={2}>
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.region')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnit('region')}
                  value={unit.region || ''}
                  error={errors.region}
                  readOnly={readOnly}
                  required />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.phone')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnit('phone')}
                  value={unit.phone || ''}
                  readOnly={readOnly} />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.FAX')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnit('fax')}
                  value={unit.fax || ''}
                  readOnly={readOnly} />
                <TextFieldComponent
                  labelWidth={Sizes.unitDetailsInputLabelWidth}
                  label={t('unitDetails.contactInformation.email')}
                  placeholder={t('global.writeSomething')}
                  handleChange={updateUnit('email')}
                  value={unit.email || ''}
                  readOnly={readOnly}
                />
                <MultiSelectComponent
                    labelWidth={Sizes.unitDetailsInputLabelWidth}
                    color="primary"
                    forceBackgroundColor={true}
                    label={t('unitDetails.contactInformation.activityArea')}
                    placeholder={t('global.selectOptions')}
                    handleChange={updateUnit('domains')}
                    readOnly={false}
                    renderValue={(selected) => {
                      if (selected?.length <= 0 || selected[0].toString() === '') {
                        return <Typography color={Colors.textWithOpacity} >{t('global.selectOptions')}</Typography>;
                      }
                      const selectedLabels = selected.map((value) => {
                        const selectedItem = Domain.selectItems().find((item) => item.value === value);
                        return selectedItem ? selectedItem.label : '';
                      });

                      return selectedLabels.join(', ');
                    }}
                    forceRenderValue={true}
                    values={unit?.domains ?? undefined}
                    items={Domain.selectItems().map((sk) => {
                      return {value: sk.value, label: sk.label};
                    })}
                />

                {!isSupplierUnit && (
                  <MultiDatePickerComponent
                    labelWidth={Sizes.unitDetailsInputLabelWidth}
                    label={t('unitDetails.contactInformation.extraordinaryClosingDays')}
                    placeholder={t('global.selectDates')}
                    readOnly={false}
                    handleChange={(value) => updateUnit('closingDays')(value)}
                    values={unit.closingDays}
                  />
                )}

                {!isSupplierUnit && unit.isGenericUnit != null && (
                  <CheckboxComponent label={t('unitDetail.contactInformation.isGenericUnit')} value={unit.isGenericUnit} handleChange={(v) => updateUnit('isGenericUnit')(v)} />
                )}
              </Stack>
            </Stack>
          </Stack>
          {!!unitProps && (
            <Stack direction="row" spacing={9}>
              {!!facturationMode && (
                <Stack flex={0.5}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography fontSize={18} fontWeight="700">
                      {t('unitDetails.billingMethod.title')}
                    </Typography>
                  </Stack>
                  <List>
                    <ListItem
                      key={facturationMode.id}
                      sx={{ borderBottomColor: Colors.background }}
                      disablePadding disableGutters
                      secondaryAction={!readOnly ? (
                        <IconButton onClick={() => handleFacturationMode?.(facturationMode)}>
                          <EditIcon />
                        </IconButton>
                      ) : undefined}>
                      <ListItemButton disableGutters onClick={() => handleFacturationMode?.(facturationMode)}>
                        <ListItemText
                          primary={facturationMode.name}
                          primaryTypographyProps={{ color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400' }} />
                      </ListItemButton>
                    </ListItem>
                  </List>
                </Stack>
              )}
              <Stack flex={0.5}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" height="38px">
                  <Typography fontSize={18} fontWeight="700">
                    {t('unitDetails.contacts.title')}
                  </Typography>
                  {!readOnly && !!handleManageContact && (
                    <IconButton
                      aria-label="medium-square-primary"
                      centerRipple={false}
                      onClick={() => handleManageContact(true)}>
                      <Add />
                    </IconButton>
                  )}
                </Stack>
                <List>
                  {contacts?.length ? contacts.map((contact, index) => (
                    <ListItem
                      key={contact.id}
                      divider={index < (contacts?.length || 0) - 1}
                      sx={{ borderBottomColor: Colors.background }}
                      disablePadding disableGutters
                      secondaryAction={!readOnly ? (
                        <IconButton onClick={() => setShowDeleteContact(contact)}>
                          <Delete />
                        </IconButton>
                      ) : undefined}>
                      <ListItemButton disableGutters onClick={() => handleManageContact?.(contact)}>
                        <ListItemText
                          primary={`${ContactType.label(contact.contactRole)} • ${contact.name || contact.surName ? `${contact.name ?? ''} ${contact.surName ?? ''}` : contact.title}`}
                          primaryTypographyProps={{ color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400' }} />
                      </ListItemButton>
                    </ListItem>
                  )) : (
                    <ListItem disablePadding disableGutters>
                      <ListItemText
                        primary={t('global.default')}
                        primaryTypographyProps={{ color: Colors.textWithOpacity, fontSize: 14, fontWeight: '400' }} />
                    </ListItem>
                  )}
                </List>
              </Stack>
            </Stack>
          )}
        </Stack>
        {!readOnly && (
          <Stack direction="row" spacing={2} justifyContent="center" mt="20px">
            <Button variant="outlined" color="primary" disabled={loading} onClick={handleCancel}>
              {t('global.cancel')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={loading || checkIfErrors(errors)}
              onClick={() => handleSave(unit)}>
              {t('global.save')}
            </Button>
          </Stack>
        )}
      </Stack>
      {/*{!!openBillingMethod && (*/}
      {/*  <ManageBillingMethodDrawer*/}
      {/*    billingMethod={typeof openBillingMethod !== "boolean" ? openBillingMethod : undefined}*/}
      {/*    readOnly={readOnly}*/}
      {/*    handleClose={() => setOpenBillingMethod(false)}*/}
      {/*    handleAdd={(billingMethod) => {*/}
      {/*      if (typeof openBillingMethod === "boolean") {*/}
      {/*        updateClientUnit("billingMethods")(*/}
      {/*          (clientUnit.billingMethods || []).concat({*/}
      {/*            ...billingMethod,*/}
      {/*            id: (clientUnit.billingMethods?.length || 0) + 1*/}
      {/*          })*/}
      {/*        );*/}
      {/*      } else {*/}
      {/*        updateClientUnit("billingMethods")(*/}
      {/*          (clientUnit.billingMethods || []).map((bm) => bm.id === billingMethod.id ? billingMethod : bm)*/}
      {/*        );*/}
      {/*      }*/}
      {/*      setOpenBillingMethod(false);*/}
      {/*    }}/>*/}
      {/*)}*/}
      {showDeleteContact && (
        <DeleteContactModal
          handleClose={() => setShowDeleteContact(false)}
          handleDelete={() => {
            if (typeof showDeleteContact !== 'boolean') {
              handleDeleteContact?.(showDeleteContact.id, () => setShowDeleteContact(false));
            }
          }} />
      )}
    </>
  );
};

export default OperationalUnitForm;
